import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useClaimPipelines } from '@/hooks/use-claim-pipelines';
import { cn } from '@/lib/utils';
import { CheckSquare, Plus } from 'lucide-react';
import type { ClaimPipeline } from '@/api/generated/client/models/ClaimPipeline';
import { PipelineDialog, type PipelineDraft } from './PipelineDialog';

interface WorkflowManagerProps {
  selectedPipelineId: number | undefined;
  onSelect: (id: number) => void;
  isAdmin: boolean;
}

const FALLBACK_COLOR = '#3B82F6';

export function WorkflowManager({
  selectedPipelineId,
  onSelect,
  isAdmin,
}: WorkflowManagerProps) {
  const { pipelines, createPipeline, updatePipeline, isMutating } = useClaimPipelines();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<ClaimPipeline | null>(null);

  const openCreate = () => {
    setEditingPipeline(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (draft: PipelineDraft) => {
    if (editingPipeline) {
      await updatePipeline({
        id: editingPipeline.id,
        data: { name: draft.name, description: draft.description || null, color: draft.color },
      });
    } else {
      const created = await createPipeline({
        name: draft.name,
        description: draft.description || null,
        color: draft.color,
        displayOrder: pipelines.length,
      });
      onSelect(created.id);
    }
    setDialogOpen(false);
    setEditingPipeline(null);
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          <CheckSquare className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-base font-semibold">Manage your workflows, statuses, and tasks</h3>
        </div>

        {/* Workflow pills */}
        <div className="flex flex-wrap items-center gap-2">
          {pipelines.map((pipeline) => {
            const isActive = pipeline.id === selectedPipelineId;
            const color = pipeline.color ?? FALLBACK_COLOR;
            return (
              <button
                key={pipeline.id}
                type="button"
                onClick={() => onSelect(pipeline.id)}
                className={cn(
                  'flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-transparent text-white shadow-sm'
                    : 'border-border bg-background text-foreground hover:bg-accent'
                )}
                style={isActive ? { backgroundColor: color } : undefined}
              >
                {!isActive && (
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                )}
                {pipeline.name}
              </button>
            );
          })}

          {isAdmin && (
            <Button type="button" variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              New workflow
            </Button>
          )}
        </div>
      </CardContent>

      <PipelineDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        pipeline={editingPipeline}
        onSubmit={handleSubmit}
        isSaving={isMutating}
      />
    </Card>
  );
}